import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Treatment } from '@next/arable-lib/advice';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, DIALOG_RETURN_TYPES, DialogComponent, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, startWith, Subject, take, takeUntil } from 'rxjs';
import { BasicSpray, CreateSprayApplicationData, CropAid, SprayApplication } from 'src/app/shared/models';
import { DateService } from 'src/app/shared/services/date.service';
import {
  createSprayApplication,
  deleteSprayApplication,
  loadBasicSprays,
  loadCropFieldsForSpray,
  loadSprayApplications,
  removeSelectedBlightCropField,
} from 'src/app/store/actions';
import {
  selectBasicSprays,
  selectGroupedSpraysBySelectedCropFields,
  selectIsAnySelectedCropFieldRecalculating,
  selectIsSpraysLoading,
  selectSelectedBlightCropFields,
  selectSprayApplications,
  selectCropYear,
} from 'src/app/store/selectors';

import { selectGBMTreatmentsGroupedByCropField, selectManualCropAids } from '../../../advices/store/selectors';

import { AsyncPipe, DatePipe, NgClass, NgStyle } from '@angular/common';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
// eslint-disable-next-line max-len
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { DirectivesModule } from '@next/arable-lib/shared';
import { LetDirective } from '@ngrx/component';
import { BlightAIIdentifiers } from '../../shared/constants';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';

export interface AdjustSpraysDialogData extends AbstractDialogData {
  sprayType: number;
  advices: CropFieldAdvice[];
  defaultDate?: Date;
  readOnly?: boolean;
}

@Component({
  selector: 'app-adjust-sprays-dialog',
  templateUrl: './adjust-sprays-dialog.component.html',
  styleUrls: ['./adjust-sprays-dialog.component.scss'],
  imports: [
    DialogComponent,
    NgClass,
    LetDirective,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    FormsModule,
    ReactiveFormsModule,
    MatChipListbox,
    MatChipOption,
    MatChipRemove,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatTooltip,
    MatAutocompleteTrigger,
    DirectivesModule,
    MatAutocomplete,
    MatButton,
    MatCard,
    NgStyle,
    MatCardContent,
    MatIconButton,
    MatProgressSpinner,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class AdjustSpraysDialogComponent extends AbstractDialog<AdjustSpraysDialogData> implements OnInit, OnDestroy {
  data = inject<AdjustSpraysDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef = inject<MatDialogRef<AdjustSpraysDialogComponent, AbstractDialogResultData<AdjustSpraysDialogData>>>(MatDialogRef);
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  dialog = inject(MatDialog);
  private readonly localeService = inject(LocaleService);
  readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly dateService = inject(DateService);
  private readonly cropYear = toSignal(this.store.pipe(select(selectCropYear)), {
    initialValue: new Date().getFullYear(),
  });
  protected readonly fmsReadonlyMessageKey = 'shared.diseaseTable.fmsManagedActionsMessage';

  @ViewChild('deleteColumn') deleteColumn!: TemplateRef<unknown>;

  isAnyCropFieldRecalculating$ = this.store.select(selectIsAnySelectedCropFieldRecalculating);
  selectedCropFields$ = this.store.select(selectSelectedBlightCropFields);
  groupedTreatments$ = this.store.select(selectGBMTreatmentsGroupedByCropField);
  spraysTableConfig!: TableConfig;
  hours: number[] = Array.from({ length: 24 }, (_, index) => index);
  selectedTreatment!: BasicSpray;
  spraysMatchingSprayType!: Observable<BasicSpray[]>;
  cropEmergenceDate!: Date;
  spraysMatchingSearchQuery$!: Observable<BasicSpray[]>;
  sprayApplication$!: Observable<SprayApplication[] | undefined>;
  isSpraysLoading$!: Observable<boolean>;

  gbmTreatmentsTableConfigs: TableConfig[] = [];
  gbmTreatments: Treatment[] = [];
  isOnMobile = false;
  cropAidMap = new Map<number, CropAid>();

  maxSprayDate = new Date(new Date().getFullYear(), 11, 31, 12);

  destroy$ = new Subject<boolean>();
  selectedDate!: Date;
  locale!: string;

  form: FormGroup = new FormGroup({
    date: new FormControl(this.data.defaultDate ?? this.dateService.now()),
    time: new FormControl(null),
    treatment: new FormControl<string | BasicSpray>('', [Validators.required]),
  });

  displayedColumns = ['date', 'time', 'sprayNumber', 'fungicide'];
  sprayHistoryColumns = this.data.readOnly ? this.displayedColumns : [...this.displayedColumns, 'actions'];
  groupedSprays$ = this.store.select(selectGroupedSpraysBySelectedCropFields);
  isAddSprayDisabled$!: Observable<boolean>;
  hasExistingSprayForDate$!: Observable<boolean>;

  ngOnInit(): void {
    const cropAids$ = this.store.pipe(select(selectManualCropAids));
    this.sprayApplication$ = this.store.select(selectSprayApplications);
    this.setDefaultTime();
    if (this.data.readOnly) {
      this.form.disable({ emitEvent: false });
    }

    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });

    this.hasExistingSprayForDate$ = combineLatest([
      this.sprayApplication$.pipe(filter(Boolean)),
      this.selectedCropFields$,
      this.form.controls.date.valueChanges.pipe(startWith(this.form.controls.date.value)),
    ]).pipe(
      takeUntil(this.destroy$),
      map(([sprayApplications, selectedCropFields, _]) => {
        if (!sprayApplications?.length) {
          return false;
        }
        const selectedDate = this.dateService.getUTCDate(this.form.controls.date.value).toDateString();
        const cropFieldsIds = selectedCropFields?.map((cf) => (cf.id));

        return sprayApplications
          .filter((spray) => cropFieldsIds.includes(spray.cropFieldId))
          .some((spray) => this.dateService.getUTCDate(spray.sprayedOn).toDateString() === selectedDate);
      }),
    );

    this.isSpraysLoading$ = this.store.select(selectIsSpraysLoading);

    this.isAddSprayDisabled$ = combineLatest([
      this.isSpraysLoading$,
      this.isAnyCropFieldRecalculating$,
      this.hasExistingSprayForDate$,
      this.form.valueChanges.pipe(startWith(this.form.value)),
      this.selectedCropFields$,
    ]).pipe(
      map(
        ([isSpraysLoading, isAnyCropFieldRecalculating, hasExistingSprayForDate, form, selectedCropFields]) => {
          const cropFieldsIds = selectedCropFields?.map((cf) => (cf.id));

          return isSpraysLoading ||
            isAnyCropFieldRecalculating ||
            hasExistingSprayForDate ||
            Boolean(this.data.readOnly) ||
            !this.form.valid ||
            !form.treatment?.id ||
            this.data.advices
              .filter((bd) => cropFieldsIds.includes(bd.cropFieldId))
              .some(
                (bd) => !bd.emergenceDate || !bd.harvestDate ||
                  !this.dateService.isInThePast(new Date(bd.emergenceDate)) ||
                  !this.dateService.isInTheFuture(new Date(bd.harvestDate)),
              );
        },
      ),
    );

    cropAids$.subscribe((cropAids) => {
      this.cropAidMap = new Map<number, CropAid>(cropAids.map((cropAid) => [cropAid.id, cropAid]));
    });

    this.breakpoint.ltMd$.pipe(take(1)).subscribe((isMobile) => {
      this.isOnMobile = isMobile;
    });

    this.store.dispatch(loadBasicSprays({ date: new Date(this.dateService.now()) }));
    this.store.dispatch(loadSprayApplications({ year: this.cropYear() }));

    this.spraysMatchingSprayType = combineLatest([
      this.store.select(selectBasicSprays),
      this.form.controls.date.valueChanges.pipe(startWith(this.dateService.now())),
    ]).pipe(
      map(([sprays, selectedDate]) => {
        if (selectedDate && (!this.selectedDate || new Date(selectedDate).toISOString() !== new Date(this.selectedDate).toISOString())) {
          this.store.dispatch(loadCropFieldsForSpray({ date: new Date(selectedDate).toISOString() }));
          this.store.dispatch(loadBasicSprays({ date: new Date(selectedDate) }));
        }
        this.selectedDate = selectedDate;
        return sprays
          .filter((s) => s.protectionCategory === this.data.sprayType)
          .sort((a, b) => Number(b.preferred) - Number(a.preferred) || a.name.localeCompare(b.name));
      }),
    );

    this.spraysMatchingSearchQuery$ = combineLatest([this.spraysMatchingSprayType, this.form.controls.treatment.valueChanges.pipe(startWith(''))]).pipe(
      map(([availableSprays, value]) => {
        if (typeof value === 'string') {
          return this.getFilteredSprays(value, [...availableSprays]);
        } else {
          return availableSprays;
        }
      }),
    );
  }

  formatFungicide(fracGroups: string[]): string {
    return fracGroups.join(' + ');
  }

  addSprayApplication() {
    if (this.data.readOnly) {
      return;
    }

    const time = this.form.controls.time.value;
    const tempDate = new Date(this.form.controls.date.value);
    const date = new Date(Date.UTC(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate(), time, 0));
    const selectedSpray = this.form.controls.treatment.value;

    const sprayApplication: CreateSprayApplicationData = {
      sprayId: selectedSpray.id,
      cropFieldId: 0,
      cropYear: date.getFullYear(),
      sprayedOn: date,
    };

    this.store.dispatch(createSprayApplication({ sprayApplication }));
    this.form.controls.treatment.reset();
  }

  deleteSprayApplication(sprayApplication: SprayApplication, cropFieldId: number) {
    if (this.data.readOnly) {
      return;
    }

    this.dialogService.confirm({
      title: this.localeService.translate('blight.sprays.deleteConfirmMessage'),
      pushHistoryState: false,
    }).subscribe((result) => {
      if (result.status === DIALOG_RETURN_TYPES.confirm) {
        this.ai.trackEvent(BlightAIIdentifiers.deleteSpray, {})
        this.store.dispatch(deleteSprayApplication({
          sprayApplicationId: sprayApplication.id,
          year: this.dateService.currentYear(),
          cropFieldId,
        }));
      }
    });
  }

  getFilteredSprays(name: string, availableSprays: BasicSpray[]): BasicSpray[] {
    const filterValue = name.toLowerCase();

    return availableSprays.filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  getBasicSprayDisplayValue(basicSpray: BasicSpray): string {
    if (!basicSpray) {
      return '';
    }

    const fracGroupsString = basicSpray.fracGroups.length > 1 ? `(${basicSpray.fracGroups.join(' + ')})` : '';
    return `${basicSpray.name} ${fracGroupsString}`;
  }


  filterGBMTreatmentsWithPhytophthora(gbmTreatments: Treatment[]): Treatment[] {
    return gbmTreatments.filter((treatment) => treatment.aidAdvices.some((advice) => {
      const cropAid = this.cropAidMap.get(advice.cropAidId);
      return Boolean(cropAid?.cropProtection?.isPhytophthora);
    }));
  }

  setDefaultTime(): void {
    const now = new Date();
    if (now.getMinutes() < 30) {
      this.form.controls.time.setValue(now.getHours());
    } else {
      this.form.controls.time.setValue(now.getHours() + 1);
    }
  }

  removeCropField(cropFieldId: number): void {
    if (this.data.readOnly) {
      return;
    }

    this.store.dispatch(removeSelectedBlightCropField({ cropFieldId }));
  }

  disableAddSpray(): boolean {
    return Boolean(this.data.readOnly) ||
      !this.form.valid ||
      !this.form.controls['treatment'].value?.id ||
      this.data.advices.some((bd) => !bd.emergenceDate || !bd.harvestDate ||
        !this.dateService.isInThePast(new Date(bd.emergenceDate)) ||
        !this.dateService.isInTheFuture(new Date(bd.harvestDate)));
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
